/**
 * ProvincialKPIs — real government data from data.go.th.
 * Replaces the stub "MUNICIPALITY OVERVIEW" that showed mocked numbers.
 *
 * Data sources:
 *   Population  — สำนักทะเบียน
 *   Tourism     — สำนักงานการท่องเที่ยวและกีฬาจังหวัดยะลา
 *   Hotel       — monthly occupancy %
 *   Accidents   — สถิติอุบัติเหตุรายอำเภอ
 *   Welfare     — ผู้สูงอายุ + ผู้พิการ (Yala province sample)
 */

import { fmtN } from "../lib/provincial";

const MONTH_TH = ["", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

interface Kpi {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}

export interface ProvincialKPIs {
  cityPopulation?: { total: number; male: number; female: number; year: number } | null;
  population: { total: number; male: number; female: number; year: number } | null;
  tourism: {
    year: number | null;
    totalVisitors: number | null;
    thaiVisitors: number | null;
    foreignVisitors: number | null;
    revenueMillionBaht: number | null;
    topForeignNationality: string | null;
    topForeignCount: number | null;
  } | null;
  hotel: { occupancyPct: number | null; guestsThisMonth: number | null; year: number; month: number } | null;
  accidents: { incidents: number; injured: number; deaths: number; per100k: number | null; year: number } | null;
  hotspotDistrict: { name: string; deaths: number; year: number } | null;
  welfare: { elderly: number; disabled: number } | null;
}

import { PanelHeader } from "./PanelHeader";
import type { FallbackTier } from "@yala/shared";
import { useT } from "../hooks/useLang";

interface Props {
  data: ProvincialKPIs | null;
  loading: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

/**
 * Two-sided male/female population bar. Renders nothing when either count is
 * missing or non-positive — never crashes on partial data.
 */
function SexSplitBar({ male, female, year }: { male?: number | null; female?: number | null; year?: number }) {
  const t = useT();
  if (male == null || female == null || male <= 0 || female <= 0) return null;
  const total = male + female;
  const malePct = (male / total) * 100;
  const femalePct = 100 - malePct;
  return (
    <div className="col" style={{ gap: 2 }} role="img" aria-label={`Population by sex: ${fmtN(male)} male, ${fmtN(female)} female`}>
      <div className="spread" style={{ alignItems: "baseline" }}>
        <div className="eyebrow">{t("SEX SPLIT", "สัดส่วนเพศ")}{year ? ` · ${year}` : ""}</div>
        <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
          ♂ {malePct.toFixed(0)}% · ♀ {femalePct.toFixed(0)}%
        </div>
      </div>
      <div style={{ display: "flex", height: 4, width: "100%" }}>
        <div style={{ width: `${malePct}%`, background: "var(--accent)" }} />
        <div style={{ width: `${femalePct}%`, background: "var(--text-3)" }} />
      </div>
      <div className="spread eyebrow mono" style={{ color: "var(--text-3)" }}>
        <span>♂ {fmtN(male)}</span>
        <span>♀ {fmtN(female)}</span>
      </div>
    </div>
  );
}

function KpiTile({ label, value, sub, color }: Kpi) {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 1 }}
      role="status"
      aria-label={`${label}: ${value}${sub ? `, ${sub}` : ""}`}
    >
      <div className="eyebrow">{label}</div>
      <div className="mono" style={{ fontSize: "var(--size-h2)", color: color ?? "var(--text)", lineHeight: 1.05 }}>
        {value}
      </div>
      {sub && <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>{sub}</div>}
    </div>
  );
}

export function ProvincialKPIs({ data, loading, ageMinutes, fallbackTier }: Props) {
  const t = useT();
  const provinceTitle = t("YALA PROVINCE // DATA.GO.TH", "จังหวัดยะลา // DATA.GO.TH");
  if (loading && !data) {
    return (
      <div className="col" role="status" aria-busy="true" aria-label="Loading provincial KPIs">
        <PanelHeader title={provinceTitle} ageMinutes={ageMinutes} fallbackTier={fallbackTier} source="data.go.th" />
        <div className="skeleton" style={{ height: 28, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 28, marginTop: 6 }} />
      </div>
    );
  }
  if (!data) return null;

  const { cityPopulation, population, tourism, hotel, accidents, hotspotDistrict, welfare } = data;

  const kpis: Kpi[] = [];

  // Lead with the CITY municipality registry — the unit the dashboard governs.
  if (cityPopulation) {
    kpis.push({
      label: t("CITY POPULATION", "ประชากรเทศบาล"),
      value: fmtN(cityPopulation.total),
      sub: `♂ ${fmtN(cityPopulation.male)} · ♀ ${fmtN(cityPopulation.female)} · ${t("municipality", "เทศบาลนคร")} · ${cityPopulation.year}`,
    });
  }
  if (population) {
    kpis.push({
      label: cityPopulation ? t("PROVINCE POP.", "ประชากรจังหวัด") : t("POPULATION", "ประชากร"),
      value: fmtN(population.total),
      sub: `♂ ${fmtN(population.male)} · ♀ ${fmtN(population.female)} · ${population.year}`,
    });
  }

  if (tourism?.totalVisitors) {
    kpis.push({
      label: t("VISITORS / YEAR", "นักท่องเที่ยว/ปี"),
      value: fmtN(tourism.totalVisitors),
      sub: tourism.year ? `${tourism.year} · ${t(`฿${fmtN(tourism.revenueMillionBaht)}M revenue`, `รายได้ ฿${fmtN(tourism.revenueMillionBaht)} ลบ.`)}` : undefined,
      color: "var(--data)",
    });
  }

  if (tourism?.topForeignNationality && tourism.topForeignNationality !== "—") {
    kpis.push({
      label: t("TOP FOREIGN", "ต่างชาติสูงสุด"),
      value: tourism.topForeignNationality,
      sub: t(`#1 · ${fmtN(tourism.topForeignCount)} visitors`, `อันดับ 1 · ${fmtN(tourism.topForeignCount)} คน`),
    });
  }

  if (hotel?.occupancyPct != null) {
    const occ = hotel.occupancyPct;
    kpis.push({
      label: t("HOTEL OCCUPANCY", "อัตราเข้าพักโรงแรม"),
      value: `${occ.toFixed(1)}%`,
      sub: `${MONTH_TH[hotel.month] ?? hotel.month}/${hotel.year} · ${t(`${fmtN(hotel.guestsThisMonth)} guests`, `${fmtN(hotel.guestsThisMonth)} คน`)}`,
      color: occ >= 70 ? "var(--good)" : occ >= 50 ? "var(--accent)" : "var(--warn)",
    });
  }

  if (accidents) {
    kpis.push({
      label: t("ROAD DEATHS / YEAR", "เสียชีวิตจราจร/ปี"),
      value: String(accidents.deaths),
      sub: t(`${accidents.incidents} incidents · ${accidents.per100k?.toFixed(1) ?? "—"} per 100K · ${accidents.year}`, `${accidents.incidents} ครั้ง · ${accidents.per100k?.toFixed(1) ?? "—"} ต่อแสน · ${accidents.year}`),
      color: "var(--bad)",
    });
  }

  if (hotspotDistrict && hotspotDistrict.deaths > 0) {
    kpis.push({
      label: t("DEADLIEST DISTRICT", "อำเภอเสี่ยงสูงสุด"),
      value: hotspotDistrict.name.replace("อำเภอ", "").trim() || hotspotDistrict.name,
      sub: t(`${hotspotDistrict.deaths} deaths · ${hotspotDistrict.year}`, `เสียชีวิต ${hotspotDistrict.deaths} · ${hotspotDistrict.year}`),
      color: "var(--warn)",
    });
  }

  if (welfare) {
    kpis.push({
      label: t("VULNERABLE PERSONS", "กลุ่มเปราะบาง"),
      value: fmtN(welfare.elderly + welfare.disabled),
      sub: t(`${fmtN(welfare.elderly)} elderly · ${fmtN(welfare.disabled)} disabled`, `ผู้สูงอายุ ${fmtN(welfare.elderly)} · ผู้พิการ ${fmtN(welfare.disabled)}`),
    });
  }

  return (
    <div className="col" style={{ gap: 8 }}>
      <PanelHeader title={provinceTitle} ageMinutes={ageMinutes} fallbackTier={fallbackTier} source="data.go.th" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
        {kpis.map((k) => <KpiTile key={k.label} {...k} />)}
      </div>
      {(() => {
        const pop = cityPopulation ?? population;
        return pop ? <SexSplitBar male={pop.male} female={pop.female} year={pop.year} /> : null;
      })()}
      <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
        {t("SOURCE", "แหล่งข้อมูล")} · DATA.GO.TH · สำนักงานจังหวัดยะลา + กระทรวงท่องเที่ยวและกีฬา
      </div>
    </div>
  );
}
